import { useMemo } from "react";
import { Search, X, ChevronDown, User, Flag, Layers } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { EMPTY_FILTERS, PRIORITY_LABELS, activeFilterCount } from "../lib/taskFilters";

const FilterMenu = ({ id, icon: Icon, label, options, selected, onChange }) => {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const active = selected.length > 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`filter-${id}-trigger`}
          className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-colors ${active
            ? "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#0066CC] dark:text-[#5AA9FF]"
            : "bg-white dark:bg-[#232326] border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5"}`}>
          <Icon size={13} /> {label}
          {active && <span data-testid={`filter-${id}-count`} className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#007AFF] text-white text-[10px] flex items-center justify-center">{selected.length}</span>}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-xl min-w-[200px] max-h-72 overflow-y-auto">
        {options.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Seçenek yok</div>}
        {options.map((o) => (
          <DropdownMenuCheckboxItem key={o.value} data-testid={`filter-${id}-option`} checked={selected.includes(o.value)}
            onCheckedChange={() => toggle(o.value)} onSelect={(e) => e.preventDefault()} className="rounded-lg text-sm">
            {o.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TaskFilterBar = ({ tasks, board, filters, onChange, visibleCount }) => {
  const opts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
    const used = new Set(tasks.flatMap((t) => (t.assignees || []).map((a) => `${a.type}:${a.id}`)));
    const assignees = [
      ...(board?.teams || []).filter((t) => used.has(`team:${t.id}`)).map((t) => ({ value: `team:${t.id}`, label: `${t.name} (grup)` })),
      ...(board?.members || []).filter((u) => used.has(`user:${u.id}`)).map((u) => ({ value: `user:${u.id}`, label: u.name })),
    ];
    if (tasks.some((t) => !(t.assignees || []).length)) assignees.push({ value: "none", label: "Atanmamış" });
    return {
      assignees,
      groups: uniq(tasks.map((t) => t.group)).map((v) => ({ value: v, label: v })),
      priorities: ["high", "medium", "low"].map((v) => ({ value: v, label: PRIORITY_LABELS[v] })),
    };
  }, [tasks, board]);
  const count = activeFilterCount(filters);
  const set = (k) => (v) => onChange({ ...filters, [k]: v });

  return (
    <div data-testid="task-filter-bar" className="flex items-center gap-2 flex-wrap mb-4">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="filter-search-input" value={filters.search} onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Task ara…"
          className="h-8 w-52 pl-8 pr-7 rounded-lg text-xs bg-white dark:bg-[#232326] border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
        {filters.search && (
          <button data-testid="filter-search-clear" onClick={() => onChange({ ...filters, search: "" })}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><X size={12} /></button>
        )}
      </div>
      <FilterMenu id="assignee" icon={User} label="Atanan" options={opts.assignees} selected={filters.assignees} onChange={set("assignees")} />
      <FilterMenu id="priority" icon={Flag} label="Öncelik" options={opts.priorities} selected={filters.priorities} onChange={set("priorities")} />
      <FilterMenu id="group" icon={Layers} label="Grup" options={opts.groups} selected={filters.groups} onChange={set("groups")} />
      {count > 0 && (
        <>
          <span data-testid="filter-result-count" className="text-xs text-slate-500 dark:text-slate-400 ml-1">{visibleCount}/{tasks.length} task</span>
          <button data-testid="filter-clear-button" onClick={() => onChange(EMPTY_FILTERS)}
            className="h-8 px-2.5 rounded-lg text-xs font-semibold text-[#FF2D55] hover:bg-[#FF2D55]/10 flex items-center gap-1 transition-colors">
            <X size={12} /> Temizle
          </button>
        </>
      )}
    </div>
  );
};

export default TaskFilterBar;
